/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Event system that mimics AngularJS $rootScope event handling
 * Provides $on, $broadcast, and $emit methods for event communication
 */
export class EventSystem {
  private listeners: Map<string, Set<(event: any, data: any) => void>> = new Map()

  /**
   * Register an event listener
   * @param eventName - Name of the event to listen for
   * @param callback - Function to call when event is triggered
   * @returns Unsubscribe function to remove the listener
   */
  $on(eventName: string, callback: (event: any, data: any) => void): () => void {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set())
    }

    const eventListeners = this.listeners.get(eventName)!
    eventListeners.add(callback)

    // Return unsubscribe function
    return () => {
      eventListeners.delete(callback)
      if (eventListeners.size === 0) {
        this.listeners.delete(eventName)
      }
    }
  }

  /**
   * Broadcast an event to all listeners
   * @param eventName - Name of the event to broadcast
   * @param data - Data to pass to listeners
   */
  $broadcast(eventName: string, data?: any): void {
    const eventListeners = this.listeners.get(eventName)
    if (eventListeners) {
      const event = { name: eventName, defaultPrevented: false }
      eventListeners.forEach((callback) => {
        try {
          callback(event, data)
        } catch (error) {
          console.error(`Error in event listener for ${eventName}:`, error)
        }
      })
    }
  }

  /**
   * Emit an event to all listeners (alias for $broadcast in this implementation)
   * @param eventName - Name of the event to emit
   * @param data - Data to pass to listeners
   */
  $emit(eventName: string, data?: any): void {
    this.$broadcast(eventName, data)
  }

  /**
   * Clear all event listeners (useful for testing)
   */
  clear(): void {
    this.listeners.clear()
  }
}

export default EventSystem
